import {
  Alert,
  Form as GenericForm,
  Input,
  Modal,
  Select,
  Switch,
  message,
} from "antd";
import * as EmailValidator from "email-validator";
import { ReadonlyArray, Runtime, flow, pipe } from "fp";
import React, { useState } from "react";

import { EuiButton, EuiConfirmModal } from "@equipmentshare/ds2";
import { Icon as DsIcon } from "@equipmentshare/ds-icon";
import { heapEvent } from "@equipmentshare/heaper";

import { useDispatch } from "~/Action";
import { noBranchDisplay } from "~/Branch";
import { Breadcrumb as GenericBreadcrumb } from "~/Breadcrumb";
import { azure, steel, veryWhiteGrey } from "~/Color";
import { Company } from "~/Company";
import { useContext } from "~/Context";
import * as Driver from "~/Driver";
import { HoursOfService as GenericHoursOfService } from "~/HoursOfService";
import * as Icon from "~/Icon";
import * as Layout from "~/Layout";
import * as GenericLink from "~/Link";
import * as Loading from "~/Loading";
import { Location as GenericLocation } from "~/Location";
import { Remote } from "~/Remote";
import { Request } from "~/Request";
import { Route as GenericRoute } from "~/Route";
import { Time } from "~/Time";
import { Toast } from "~/Toast";
import * as Typography from "~/Typography";


export type AddEdit = Partial<Time.Zone> &
  Partial<Company.Location & GenericHoursOfService> &
  GenericLocation.State & {
    readonly driver?: Driver.Driver;
    readonly email?: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly phoneNumber: string;
    readonly license: string;
    readonly hasBerth?: boolean;
  };

export function AddEdit({ driver }: AddEdit.Props) {
  return pipe(
    Driver.useFromReference({
      ...driver,
      found: (driver) => driver,
      missing: () => driver,
    }),
    (driver) => (
      <Layout.Page.Component all>
        <AddEdit.Breadcrumb driver={driver} />
        <Layout.Section.Component>
          <Layout.Section.Header.Component
            Left={() => (
              <>
                <Typography.H2.Component all={false}>
                  {driver
                    ? Driver.Name.toString(driver.name) ?? <Loading.Component />
                    : "Add driver"}
                </Typography.H2.Component>
                {driver ? <T3ProfileLink driver={driver} /> : null}
              </>
            )}
          ></Layout.Section.Header.Component>
          <Layout.Block.Component top left right>
            <AddEdit.Form driver={driver} />
          </Layout.Block.Component>
        </Layout.Section.Component>
      </Layout.Page.Component>
    )
  );
}

function T3ProfileLink({ driver }: { readonly driver: Driver.Driver }) {
  const { baseURL } = useContext();

  return (
    <a
      href={`${baseURL}/#/company-admin/users/edit/${driver?.id}`}
      target="_blank"
      style={{ marginLeft: "2rem" }}
    >
      View this driver's T3 user profile <Icon.ExternalLinkSquareAlt />
    </a>
  );
}

export namespace AddEdit {
  export type Props = Route;

  export const use = (emailAvailable: boolean, driver?: Driver.Driver) => {
    return Remote.Mutation.use(
      driver === undefined && emailAvailable ? Post.request : Patch.request
    );
  };

  export const useCheckEmailExits = (email?: string, execute = false) => {
    return Remote.when<Form.Email.Status, Form.Email.Status>(
      Form.Email.checkExists(email || "", execute),
      {
        error: () => ({ status: "pending" }),
        loading: () => ({ status: "pending" }),
        success: (result) => result,
      }
    );
  };

  export const useResetPasswordRequest = () =>
    Remote.Mutation.use(Post.resetPasswordRequest);

  export type Route = { readonly driver?: Driver.Driver };
  export namespace Route {
    export const definition = (type: "add" | "edit" = "add"): string =>
      `${Driver.Overview.Route.definition()}/${
        type === "add" ? "add" : `:readableID/edit`
      }`;

    export const href = (route?: Route): string =>
      `${Driver.Overview.Route.definition()}${
        route?.driver ? `/${Driver.toReadableID(route.driver)}` : ""
      }/${route?.driver ? "edit" : "add"}`;

    export const use = (route?: Route) => GenericRoute.use(href(route));
  }

  const requestConfig = ({
    firstName: first_name,
    lastName: last_name,
    email: email_address,
    phoneNumber: phone_number,
    license: license_number,
    state: { abbreviation: license_state },
    zone: timezone,
    hoursOfService,
    location,
    hasBerth = false,
    ...body
  }: AddEdit) => ({
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...body,
      email_address: email_address,
      first_name,
      last_name,
      phone_number,
      license_number,
      license_state,
      timezone,
      exemption_type_id: hoursOfService?.exemption?.id,
      hours_of_service_type_id: hoursOfService?.type?.id,
      reporting_location_id: location?.id,
      sleeper_berth: hasBerth,
    }),
  });

  const resetPasswordRequestConfig = ({
    email,
  }: {
    readonly email: string;
  }) => ({
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email,
    }),
  });

  export namespace Post {
    /** [API Documentation](http://equipmentshare.pages.internal.equipmentshare.com/equipmentshare-api/develop/index.html?highlight=drivers#post--elogs-drivers) */
    export const request = ({
      company,
      ...addEdit
    }: Company & AddEdit): Request.Fn => {
      return flow(
        Request.fromConfig({
          ...requestConfig(addEdit),
          method: "POST",
          endpoint: `/companies/${company.id}/drivers`,
        }),
        Request.Expect.status(201)
      );
    };

    export const resetPasswordRequest = ({
      email,
    }: {
      readonly email: string;
    }): Request.Fn => {
      return flow(
        Request.fromConfig({
          ...resetPasswordRequestConfig({ email }),
          method: "POST",
          endpoint: `/forgot-password-tokens`,
        }),
        Request.Expect.status(201)
      );
    };
  }

  export namespace Patch {
    /** [API Documentation](http://equipmentshare.pages.internal.equipmentshare.com/equipmentshare-api/develop/index.html?highlight=drivers#patch--elogs-drivers) */
    export const request = ({
      company,
      driver,
      user_id,
      ...addEdit
    }: Company & AddEdit & { readonly user_id?: number }): Request.Fn => {
      return flow(
        Request.fromConfig({
          ...requestConfig(addEdit),
          method: "PATCH",
          endpoint: driver
            ? `/companies/${company.id}/drivers/${driver?.id}`
            : `/companies/${company.id}/drivers/${user_id}`,
        }),
        Request.Expect.status(200)
      );
    };
  }

  export const Breadcrumb = ({ driver }: Props) => {
    const route = Driver.Overview.Route.use();
    const goBack = () => route.go();
    return (
      <Layout.Block.Component bottom>
        <GenericBreadcrumb>
          <GenericBreadcrumb.Item>
            <GenericLink.Component onClick={goBack}>
              Drivers
            </GenericLink.Component>
          </GenericBreadcrumb.Item>
          <GenericBreadcrumb.Item>
            {driver ? <Driver.Name.Component {...driver} /> : <>Add</>}
          </GenericBreadcrumb.Item>
          {driver && <GenericBreadcrumb.Item>Edit</GenericBreadcrumb.Item>}
        </GenericBreadcrumb>
      </Layout.Block.Component>
    );
  };

  export type EmailState = {
    readonly email?: string;
    readonly addressAvailable: boolean;
    readonly userId?: number;
  };

  export function Form({ driver }: Props) {
    const [form] = GenericForm.useForm();
    const company = Company.use();
    const details = Remote.toUndefined(company.company);

    const HOSWarning = Remote.isSuccess(company.company)
      && details?.hoursOfService?.type?.id === undefined
      && <Toast timeout={60000}
        message="Company Default HOS Not Set"
        description="The default Hours of Service Rule must be set in E-Logs Company Settings before you can set up a driver." />;

    const [emailState, setEmailState] = React.useState<EmailState>({
      email: driver?.email,
      addressAvailable: false,
      userId: undefined,
    });

    const [saveInProgress, setSaveInProgress] = useState(false);

    React.useEffect(() => {
      if (!saveInProgress) {
        if (driver) {
          form.setFieldsValue({
            email: driver.email,
            "name.first": driver.name?.first,
            "name.last": driver.name?.last,
            phone: driver.phone,
            zone: driver.time?.zone,
            "license.number": driver.license,
            "license.state": driver.state?.abbreviation,
            "hoursOfService.type.id": driver.hoursOfService?.type?.id,
            "hoursOfService.exemption.id": driver.hoursOfService?.exemption?.id,
            location: driver.homeOffice?.location.id,
            hasBerth: driver.hasBerth,
          });
          return () => setEmailState({ ...emailState, email: driver?.email });
        } else if (details) {
          form.setFieldsValue({
            zone: details.time?.zone,
            "hoursOfService.type.id": details.hoursOfService?.type?.id,
            location: details.location?.id,
          });
        }
      }
    }, [driver, details]);

    const onEmailAddressChange = (
      emailAddress: string,
      available: boolean,
      userId?: number
    ) => {
      setEmailState({
        email: emailAddress,
        addressAvailable: available,
        userId,
      });
    };

    const addEditDriver = use(emailState.addressAvailable, driver);
    const warnOnSave = (onComplete: (save: boolean) => void): void => {
      Modal.confirm({
        title: "Change user email?",
        icon: (
          <DsIcon
            name="$icon_alert_line"
            color="$color_primary_red"
            size="$icon_size_5"
            layout={{
              margin: { right: "$spacing_2" },
            }}
          />
        ),
        content:
          "This action will change the user's email in their T3 user profile. They will need to use the updated email address to log in. Do you wish to continue?",
        okText: "Save",
        okButtonProps: {
          style: {
            borderRadius: "3px",
          },
        },
        cancelText: "Cancel",
        cancelButtonProps: {
          danger: false,
          ghost: false,
          style: {
            borderRadius: "3px",
          },
        },
        onOk() {
          onComplete(true);
        },
        onCancel() {
          onComplete(false);
        },
      });
    };

    const onSave = () => {
      if (driver?.email && form.getFieldValue("email") !== driver?.email) {
        warnOnSave((shouldSave) => {
          shouldSave && onSuccess();
        });
      } else {
        onSuccess();
      }
    };

    const onSuccess = () => {
      setSaveInProgress(true);
      return addEditDriver.request({
        ...company,
        driver,
        user_id: emailState.userId,
        email:
          form.getFieldValue("email") !== driver?.email
            ? form.getFieldValue("email")
            : undefined,
        firstName: form.getFieldValue("name.first"),
        lastName: form.getFieldValue("name.last"),
        phoneNumber: form.getFieldValue("phone"),
        zone: form.getFieldValue("zone"),
        license: form.getFieldValue("license.number"),
        state: { abbreviation: form.getFieldValue("license.state") },
        hasBerth: form.getFieldValue("hasBerth"),
        location: { id: form.getFieldValue("location") },
        hoursOfService: {
          type: { id: form.getFieldValue("hoursOfService.type.id") },
          exemption: { id: form.getFieldValue("hoursOfService.exemption.id") },
        },
      });
    };

    const dispatch = useDispatch();
    const route = Driver.Overview.Route.use();

    React.useEffect(() => {
      if (saveInProgress) {
        void Remote.when(addEditDriver.response, {
          idle: () => undefined,
          loading: () => undefined,
          error: (error) => {
            void message.error(
              "There was an error updating or creating a driver!",
              5
            );
            console.warn(error);
            setSaveInProgress(false);
            return undefined;
          },
          success: () => {
            void message.success("All set!", 5);
            dispatch(Driver.Action.Requested.create());
            route.go();
            setSaveInProgress(false);
            return undefined;
          },
        });
      }
    }, [saveInProgress, addEditDriver.response]);

    return (
      <GenericForm
        form={form}
        onFinish={onSave}
        validateMessages={{ required: "This field is required" }}
        layout="vertical"
        requiredMark="optional"
        autoComplete="off"
      >
        <Layout.Row.Component gutter={16}>
          {HOSWarning}
          <Layout.Column.Component span={12}>
            <Form.Email
              form={form}
              driver={driver}
              initialValue={emailState.email}
              onChange={onEmailAddressChange}
            />
            <Form.Name />
            <Form.Phone />
            <Form.TimeZone form={form} />
            <Form.PasswordResetButton driver={driver} />
          </Layout.Column.Component>
          <Layout.Column.Component span={12}>
            <Form.License />
            <Form.HoursOfService form={form} />
            <Form.Location form={form} />
            {driver ? (
              <Layout.Row.Component>
                <Layout.Column.Component xs={12} lg={11} xxl={8}>
                  <Form.HasBerth />
                </Layout.Column.Component>
                <Layout.Column.Component span={12}>
                  <Form.DriverBranch driver={driver} />
                </Layout.Column.Component>
              </Layout.Row.Component>
            ) : (
              <Form.HasBerth />
            )}
          </Layout.Column.Component>
          <Layout.Column.Component span={24} style={{ textAlign: "right" }}>
            <Layout.Block.Component bottom>
              <Form.Actions
                isLoading={Remote.isLoading(addEditDriver.response)}
              />
            </Layout.Block.Component>
          </Layout.Column.Component>
        </Layout.Row.Component>
      </GenericForm>
    );
  }
  export namespace Form {
    export const ConfirmModal = ({
      emailAddress,
      onConfirm,
    }: {
      readonly emailAddress: string;
      readonly onConfirm: (confirm: boolean) => void;
    }) => {
      const cancelAction = () => onConfirm(false);
      const confirmAction = () => onConfirm(true);
      return (
        <EuiConfirmModal
          title="Send Password Reset Email"
          onCancel={cancelAction}
          onConfirm={confirmAction}
          cancelButtonText="Cancel"
          confirmButtonText="Send"
          defaultFocusedButton="confirm"
          data-testid="password-reset-confirm-modal"
        >
          <p>
            {`This will send a password reset request email to ${emailAddress}`}
          </p>
        </EuiConfirmModal>
      );
    };

    export function PasswordResetButton({ driver }: Props) {
      const [showConfirmModal, setShowConfirmModal] = useState(false);
      const [resetRequestInProgress, setResetRequestInProgress] = useState(
        false
      );
      const resetRequest = useResetPasswordRequest();

      const onSend = (shouldSend: boolean): void => {
        setShowConfirmModal(false);

        if (shouldSend && driver && driver.email) {
          const { email } = driver;
          setResetRequestInProgress(true);
          heapEvent(
            "E-Logs - Add/Edit Driver - Click - Send Password Reset Request",
            {}
          );
          return resetRequest.request({ email });
        }
      };

      const confirmSend = (): void => {
        setShowConfirmModal(true);
      };

      React.useEffect(() => {
        if (resetRequestInProgress) {
          Remote.when(resetRequest.response, {
            idle: () => {},
            loading: () => {},
            error: () => {
              setResetRequestInProgress(false);
              return message.error(
                "Something went wrong while trying to send a password reset request!",
                5
              );
            },
            success: () => {
              setResetRequestInProgress(false);
              return message.success("Password reset email has been sent", 5);
            },
          });
        }
      }, [resetRequest.response, resetRequestInProgress]);

      return (
        <div>
          <EuiButton
            onClick={confirmSend}
            color="text"
            disabled={!driver?.email}
            data-testid="send-password-reset"
          >
            Send password reset
          </EuiButton>
          {showConfirmModal && driver?.email && (
            <ConfirmModal onConfirm={onSend} emailAddress={driver?.email} />
          )}
        </div>
      );
    }

    export function isDuplicateEmail(
      status?: Email.Status,
      driver?: Driver.Driver
    ): boolean {
      if (driver) {
        if (
          (status?.status == "user" || status?.status == "driver") &&
          `${status?.userId}` === driver.id
        ) {
          return false;
        }
        return !(
          status?.status === "available" || status?.status === "pending"
        );
      }
      return (
        status?.status === "driver" ||
        (status?.status === "user" && !status?.withinCompany)
      );
    }

    type EmailProps = {
      readonly driver?: Driver.Driver;
      readonly form: ReturnType<typeof GenericForm.useForm>[number];
      readonly initialValue?: string;
      readonly onChange: (
        value: string,
        availability: boolean,
        userId?: number
      ) => void;
    };

    export function Email({
      driver,
      form,
      initialValue,
      onChange,
    }: EmailProps) {
      const [email, setEmail] = useState<string | undefined>(initialValue);
      const [hasChanged, setHasChanged] = useState<boolean>(true);

      const emailIsValid = EmailValidator.validate(email ?? "");

      const availabilityStatus = useCheckEmailExits(email, emailIsValid);

      const alertMessage =
        !driver &&
        availabilityStatus?.status === "user" &&
        availabilityStatus?.withinCompany ? (
          <Alert
            type="warning"
            message="This user currently exists and will become a driver"
            showIcon
          />
        ) : undefined;

      React.useEffect(() => {
        if (email && emailIsValid) {
          if (hasChanged && availabilityStatus.status !== "pending") {
            onChange(
              email,
              availabilityStatus.status === "available",
              "userId" in availabilityStatus
                ? availabilityStatus.userId
                : undefined
            );
            void form.validateFields(["email"]);
            return () => setHasChanged(false);
          }
          void form.validateFields(["email"]);
        }
      }, [email, availabilityStatus]);

      const setEmailAddress = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
        setHasChanged(true);
      };

      return (
        <GenericForm.Item label="Email Address" required>
          <GenericForm.Item
            name="email"
            shouldUpdate
            noStyle
            rules={[
              {
                required: true,
                validator: () => {
                  return email === driver?.email ||
                    (email && EmailValidator.validate(email))
                    ? Promise.resolve()
                    : Promise.reject("Valid email address required");
                },
              },
              {
                required: true,
                validator: () => {
                  const errorMessage = isDuplicateEmail(
                    availabilityStatus,
                    driver
                  )
                    ? `${email} is already taken`
                    : undefined;
                  return errorMessage
                    ? Promise.reject(errorMessage)
                    : Promise.resolve();
                },
              },
              { type: "string", required: true },
            ]}
          >
            <Input
              data-testid="inputEmail"
              value={email}
              onChange={setEmailAddress}
              autoComplete="off"
            />
          </GenericForm.Item>
          {alertMessage}
        </GenericForm.Item>
      );
    }

    export namespace Email {
      export const checkExists = (email: string, execute: boolean) => {
        const company = Company.use();
        return Remote.Query.use(
          ["check-email-exists", email],
          request(company, email),
          { enabled: email !== "" && execute }
        );
      };
      export const fetchCheckExists = (email: string) => {
        const company = Company.use();
        return Remote.Fetch.use(
          ["check-email-exists", email],
          request(company, email),
          { enabled: email !== "" }
        );
      };

      export const request = ({ company }: Company, email: string) => {
        const endpoint = `/companies/${company.id}/user/email/${email}/exists`;
        return flow(
          Request.fromConfig({ endpoint }),
          Request.Expect.status(200, 404),
          Request.Expect.json(response()),
          Request.onSuccess(
            (response): Status => {
              return "message" in response
                ? { status: "available" }
                : pipe(
                    response,
                    ReadonlyArray.reduce(
                      {
                        isDriver: false,
                        isUserEmail: false,
                        userId: -1,
                        driverId: -1,
                        withinCompany: false,
                      },
                      (prev, next) => ({
                        isDriver: prev.isDriver || next.is_driver,
                        isUserEmail:
                          prev.isUserEmail || next.is_user_email === true,
                        driverId: next.is_driver ? next.user_id : -1,
                        userId: !next.is_driver ? next.user_id : -1,
                        withinCompany: next.is_member_of_company,
                      })
                    ),
                    ({ isDriver, userId, driverId, withinCompany }) => ({
                      status: isDriver
                        ? "driver"
                        : userId
                        ? "user"
                        : "available",
                      userId: isDriver ? driverId : userId,
                      withinCompany,
                    })
                  );
            }
          )
        );
      };

      export const response = () =>
        Runtime.union([
          Runtime.strict({ message: Runtime.nullable(Runtime.string()) }),
          Runtime.array(
            Runtime.strict({
              is_deleted: Runtime.boolean(),
              is_driver: Runtime.boolean(),
              is_user_email: Runtime.boolean(),
              user_id: Runtime.number(),
              is_member_of_company: Runtime.boolean(),
            })
          ),
        ]);

      export type Status =
        | { readonly status: "pending" | "available" }
        | {
            readonly status: "user" | "driver";
            readonly userId: number;
            readonly withinCompany: boolean;
          };
    }

    export function Name() {
      return (
        <Layout.Row.Component gutter={16}>
          <Layout.Column.Component span={12}>
            <Name.First />
          </Layout.Column.Component>
          <Layout.Column.Component span={12}>
            <Name.Last />
          </Layout.Column.Component>
        </Layout.Row.Component>
      );
    }

    export namespace Name {
      export const First = () => (
        <GenericForm.Item
          label="First name"
          name="name.first"
          rules={[{ type: "string", required: true }]}
        >
          <Input autoComplete="off" data-testid="first-name" />
        </GenericForm.Item>
      );

      export const Last = () => (
        <GenericForm.Item
          label="Last name"
          name="name.last"
          rules={[{ type: "string", required: true }]}
        >
          <Input autoComplete="off" data-testid="last-name" />
        </GenericForm.Item>
      );
    }

    export const Phone = () => (
      <GenericForm.Item
        label="Phone number"
        name="phone"
        rules={[
          {
            type: "string",
            validator: (_, value) =>
              value === undefined ||
              `${value}` == "" ||
              /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im.test(
                value
              )
                ? Promise.resolve()
                : Promise.reject("Not a valid phone number"),
          },
        ]}
      >
        <Input autoComplete="off" />
      </GenericForm.Item>
    );

    export const TimeZone = ({
      form,
    }: {
      readonly form: ReturnType<typeof GenericForm.useForm>[number];
    }) => {
      const [, forceUpdate] = React.useState<Time.Zone>();

      const selectZone = ({ zone }: { readonly zone: string }) => {
        form.setFieldsValue({ zone });
        forceUpdate({ zone });
      }

      return (
        <GenericForm.Item shouldUpdate label="Time zone" name="zone" required>
          <Time.Zone.Select
            selected={{ zone: form.getFieldValue("zone") }}
            select={selectZone}
          />
        </GenericForm.Item>
      );
    };

    export const License = () => (
      <>
        <GenericForm.Item
          label="Driver's license"
          name="license.number"
          rules={[{ type: "string", required: true }]}
        >
          <Input data-testid="license-number" />
        </GenericForm.Item>
        <GenericForm.Item
          label="Issuing state"
          data-testid="license-state"
          name="license.state"
          rules={[{ type: "string", required: true }]}
        >
          <Select
            data-testid="state-select"
            showSearch
            dropdownMatchSelectWidth={false}
            options={pipe(
              GenericLocation.State.Abbreviation.list(),
              ReadonlyArray.map((abbreviation) => ({ value: abbreviation })),
              (options) =>
                options as React.ComponentProps<typeof Select>["options"]
            )}
          />
        </GenericForm.Item>
      </>
    );

    export const HoursOfService = ({
      form,
    }: {
      readonly form: ReturnType<typeof GenericForm.useForm>[number];
    }) => {
      const setHosFieldsValue = (selected?: GenericHoursOfService.Type) => {
        form.setFieldsValue({
          "hoursOfService.type.id": selected?.type.id,
        });
      }

      const setExemptionFieldsValue = (
        exemption?: GenericHoursOfService.Exemption
      ) => {
        form.setFieldsValue({
          "hoursOfService.exemption.id": exemption?.exemption.id,
        })
      }

      return (
        <>
          <GenericForm.Item
            label="Cycle rule"
            name="hoursOfService.type.id"
            required
            rules={[{type: "string", required: true}]}
          >
            <GenericHoursOfService.Type.Select
              selected={{
                type: { id: form.getFieldValue("hoursOfService.type.id") },
              }}
              select={setHosFieldsValue}
            />
          </GenericForm.Item>
          <GenericForm.Item
            label="Exemption"
            name="hoursOfService.exemption.id"
          >
            <GenericHoursOfService.Exemption.Select
              selected={pipe(
                form.getFieldValue("hoursOfService.exemption.id"),
                (id: string | undefined) =>
                  id ? { exemption: { id } } : undefined
              )}
              select={setExemptionFieldsValue}
            />
          </GenericForm.Item>
        </>
      );
    };

    export const Location = ({
      form,
    }: {
      readonly form: ReturnType<typeof GenericForm.useForm>[number];
    }) => {
      const setLocationFieldsValue = ({ location: { id } }: Company.Location) => {
        form.setFieldsValue({ location: id });
      }

      return (
        <GenericForm.Item
          label="Work reporting location"
          name="location"
          required
        >
          <Company.Locations.SelectOne
            selected={{ location: { id: form.getFieldValue("location") } }}
            select={setLocationFieldsValue}
          />
        </GenericForm.Item>
      );
    };

    export const HasBerth = () => {
      const [hasBerth, setHasBerth] = React.useState(false);

      const toggleSleeperBerth = () => setHasBerth(!hasBerth);
      return (
        <GenericForm.Item
          valuePropName="checked"
          label="Has sleeper berth"
          name="hasBerth"
        >
          <Switch checked={hasBerth} onChange={toggleSleeperBerth} />
        </GenericForm.Item>
      );
    };

    export const DriverBranch: React.Fn<Props> = ({ driver }: Props) => {
      const { baseURL } = useContext();

      return pipe(
        Driver.useFromReference({
          id: driver?.id,
          found: (driver) => driver,
          missing: () => driver,
        }),
        (driver) => (
          <GenericForm.Item
            label="T3 branch (read only)"
            tooltip={
              <>
                The branch field can be updated in this user's T3 profile.{" "}
                <a
                  href={`${baseURL}/#/company-admin/users/edit/${driver?.id}`}
                  target="_blank"
                  style={{ color: azure(), fontWeight: "bold" }}
                >
                  T3 user profile
                </a>
              </>
            }
            required={true}
          >
            <Input
              readOnly={true}
              disabled={false}
              value={driver?.branch?.name || noBranchDisplay()}
              style={{ color: steel(), backgroundColor: veryWhiteGrey() }}
            />
          </GenericForm.Item>
        )
      );
    };

    export const Actions = ({
      isLoading,
    }: {
      readonly isLoading?: boolean;
    }) => {
      const route = Driver.Overview.Route.use();
      const navigateBack = () => route.go();
      return (
        <Layout.Space>
          <EuiButton color="text" onClick={navigateBack} disabled={isLoading}>
            Cancel
          </EuiButton>
          <EuiButton color="primary" fill disabled={isLoading} type="submit">
            Save
          </EuiButton>
        </Layout.Space>
      );
    };
  }
}
